import type { Game } from "./types.js";

interface Brick {
  x: number;
  y: number;
  w: number;
  h: number;
  row: number;
  alive: boolean;
}

interface RowColor {
  stroke: string;
  score: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  decay: number;
  size: number;
  color: string;
}

// Brand-palette ramp: --accent-1 purple → --accent-2 cyan across 6 rows.
const ROW_COLORS: RowColor[] = (() => {
  const rows: RowColor[] = [];
  const start = { h: 258, s: 90, l: 66 };
  const end = { h: 189, s: 85, l: 54 };
  const scores = [70, 50, 40, 30, 20, 10];
  for (let i = 0; i < 6; i++) {
    const t = i / 5;
    const h = start.h + (end.h - start.h) * t;
    const s = start.s + (end.s - start.s) * t;
    const l = start.l + (end.l - start.l) * t;
    rows.push({
      stroke: `hsl(${h.toFixed(0)}, ${s.toFixed(0)}%, ${l.toFixed(0)}%)`,
      score: scores[i]!,
    });
  }
  return rows;
})();

let wrap: HTMLDivElement | null = null;
let cleanupFn: (() => void) | null = null;

function buildDom(host: HTMLElement): {
  wrap: HTMLDivElement;
  canvas: HTMLCanvasElement;
  overlayEl: HTMLDivElement;
  startBtn: HTMLButtonElement;
} {
  const wrapEl = document.createElement("div");
  wrapEl.className = "breakout-wrap";

  const canvas = document.createElement("canvas");
  canvas.width = 800;
  canvas.height = 600;
  canvas.className = "breakout-canvas";
  canvas.setAttribute("aria-hidden", "true");

  const overlayEl = document.createElement("div");
  overlayEl.className = "breakout-overlay";
  overlayEl.innerHTML = `
    <h1 class="breakout-overlay__title">BREAKOUT</h1>
    <p>Move with your <kbd>mouse</kbd> or <kbd>&larr;</kbd> <kbd>&rarr;</kbd></p>
    <p>Launch ball: <kbd>click</kbd> or <kbd>space</kbd></p>
    <p>Pause: <kbd>P</kbd></p>
    <button class="breakout-overlay__btn" type="button">START</button>
  `;

  const startBtn = overlayEl.querySelector<HTMLButtonElement>(".breakout-overlay__btn")!;

  wrapEl.appendChild(canvas);
  wrapEl.appendChild(overlayEl);
  host.appendChild(wrapEl);

  return { wrap: wrapEl, canvas, overlayEl, startBtn };
}

function mount(host: HTMLElement): void {
  const dom = buildDom(host);
  wrap = dom.wrap;
  const { canvas, overlayEl, startBtn } = dom;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const W = canvas.width;
  const H = canvas.height;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const STATE = { MENU: 0, READY: 1, PLAYING: 2, PAUSED: 3, GAMEOVER: 4, WIN: 5 } as const;
  type StateValue = (typeof STATE)[keyof typeof STATE];
  let state: StateValue = STATE.MENU;
  let score = 0;
  let lives = 3;
  let level = 1;
  let highScore = 0;

  const paddle = { w: 110, h: 14, x: W / 2 - 55, y: H - 50, speed: 9 };
  const ball = { x: W / 2, y: H - 64, r: 8, vx: 0, vy: 0, speed: 6, stuck: true };

  const BRICK_ROWS = 6;
  const BRICK_COLS = 11;
  const BRICK_W = 60;
  const BRICK_H = 22;
  const BRICK_GAP = 4;
  const BRICK_OFFSET_X = (W - (BRICK_COLS * (BRICK_W + BRICK_GAP) - BRICK_GAP)) / 2;
  const BRICK_OFFSET_Y = 70;

  let bricks: Brick[] = [];
  const particles: Particle[] = [];
  const keys: Record<string, boolean> = {};
  let mouseX: number | null = null;
  let raf: number | null = null;

  function buildBricks(): void {
    bricks = [];
    for (let r = 0; r < BRICK_ROWS; r++) {
      for (let c = 0; c < BRICK_COLS; c++) {
        bricks.push({
          x: BRICK_OFFSET_X + c * (BRICK_W + BRICK_GAP),
          y: BRICK_OFFSET_Y + r * (BRICK_H + BRICK_GAP),
          w: BRICK_W,
          h: BRICK_H,
          row: r,
          alive: true,
        });
      }
    }
  }

  function spawnParticles(x: number, y: number, color: string, count = 18): void {
    if (reduced) return;
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = 1 + Math.random() * 4;
      particles.push({
        x, y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life: 1,
        decay: 0.02 + Math.random() * 0.03,
        size: 2 + Math.random() * 3,
        color,
      });
    }
  }

  function updateParticles(): void {
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i]!;
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.08;
      p.vx *= 0.98;
      p.life -= p.decay;
      if (p.life <= 0) particles.splice(i, 1);
    }
  }

  function drawParticles(): void {
    for (const p of particles) {
      ctx!.globalAlpha = Math.max(0, p.life);
      ctx!.fillStyle = p.color;
      ctx!.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
    ctx!.globalAlpha = 1;
  }

  function startGame(): void {
    score = 0;
    lives = 3;
    level = 1;
    paddle.w = 110;
    buildBricks();
    resetBall();
    state = STATE.READY;
    overlayEl.classList.add("hidden");
  }

  function nextLevel(): void {
    level++;
    paddle.w = Math.max(70, 110 - (level - 1) * 8);
    ball.speed = Math.min(10, 6 + (level - 1) * 0.6);
    buildBricks();
    resetBall();
    state = STATE.READY;
  }

  function resetBall(): void {
    ball.stuck = true;
    ball.x = paddle.x + paddle.w / 2;
    ball.y = paddle.y - ball.r - 1;
    ball.vx = 0;
    ball.vy = 0;
  }

  function launchBall(): void {
    if (!ball.stuck) return;
    ball.stuck = false;
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.5;
    ball.vx = Math.cos(angle) * ball.speed;
    ball.vy = Math.sin(angle) * ball.speed;
    state = STATE.PLAYING;
  }

  function showOverlay(title: string, sub: string, btnText = "PLAY AGAIN"): void {
    overlayEl.classList.remove("hidden");
    const h1 = overlayEl.querySelector("h1")!;
    h1.textContent = title;
    const ps = overlayEl.querySelectorAll("p");
    ps[0]!.innerHTML = sub;
    ps[1]!.innerHTML = `Score: <b>${score}</b> &nbsp; Best: <b>${highScore}</b>`;
    ps[2]!.innerHTML = `Level reached: <b>${level}</b>`;
    startBtn.textContent = btnText;
  }

  function roundRect(x: number, y: number, w: number, h: number, r: number): void {
    ctx!.beginPath();
    ctx!.moveTo(x + r, y);
    ctx!.arcTo(x + w, y, x + w, y + h, r);
    ctx!.arcTo(x + w, y + h, x, y + h, r);
    ctx!.arcTo(x, y + h, x, y, r);
    ctx!.arcTo(x, y, x + w, y, r);
    ctx!.closePath();
  }

  function update(): void {
    if (state === STATE.PAUSED || state === STATE.MENU ||
        state === STATE.GAMEOVER || state === STATE.WIN) return;

    if (mouseX !== null) {
      paddle.x += (mouseX - (paddle.x + paddle.w / 2)) * 0.35;
    }
    if (keys["arrowleft"] || keys["a"]) paddle.x -= paddle.speed;
    if (keys["arrowright"] || keys["d"]) paddle.x += paddle.speed;
    paddle.x = Math.max(0, Math.min(W - paddle.w, paddle.x));

    if (ball.stuck) {
      ball.x = paddle.x + paddle.w / 2;
      ball.y = paddle.y - ball.r - 1;
      return;
    }

    const steps = 2;
    for (let s = 0; s < steps; s++) {
      ball.x += ball.vx / steps;
      ball.y += ball.vy / steps;
      if (ball.x - ball.r < 0) { ball.x = ball.r; ball.vx *= -1; }
      if (ball.x + ball.r > W) { ball.x = W - ball.r; ball.vx *= -1; }
      if (ball.y - ball.r < 0) { ball.y = ball.r; ball.vy *= -1; }

      if (ball.vy > 0 &&
          ball.y + ball.r >= paddle.y &&
          ball.y + ball.r <= paddle.y + paddle.h + 6 &&
          ball.x >= paddle.x - ball.r &&
          ball.x <= paddle.x + paddle.w + ball.r) {
        ball.y = paddle.y - ball.r;
        const hit = (ball.x - (paddle.x + paddle.w / 2)) / (paddle.w / 2);
        const maxAngle = Math.PI * 0.4;
        const angle = -Math.PI / 2 + hit * maxAngle;
        const speed = Math.hypot(ball.vx, ball.vy);
        ball.vx = Math.cos(angle) * speed;
        ball.vy = Math.sin(angle) * speed;
      }

      for (const b of bricks) {
        if (!b.alive) continue;
        if (ball.x + ball.r > b.x && ball.x - ball.r < b.x + b.w &&
            ball.y + ball.r > b.y && ball.y - ball.r < b.y + b.h) {
          const prevX = ball.x - ball.vx / steps;
          const prevY = ball.y - ball.vy / steps;
          const fromLeft = prevX + ball.r <= b.x;
          const fromRight = prevX - ball.r >= b.x + b.w;
          const fromTop = prevY + ball.r <= b.y;
          const fromBottom = prevY - ball.r >= b.y + b.h;

          if (fromLeft || fromRight) ball.vx *= -1;
          else if (fromTop || fromBottom) ball.vy *= -1;
          else ball.vy *= -1;

          b.alive = false;
          const color = ROW_COLORS[b.row]!;
          score += color.score;
          if (score > highScore) highScore = score;
          spawnParticles(b.x + b.w / 2, b.y + b.h / 2, color.stroke, 20);
          break;
        }
      }

      if (ball.y - ball.r > H) {
        lives--;
        if (lives <= 0) {
          state = STATE.GAMEOVER;
          showOverlay("GAME OVER", "The ball got past you.", "PLAY AGAIN");
          return;
        } else {
          resetBall();
          state = STATE.READY;
          return;
        }
      }
    }

    if (bricks.every((b) => !b.alive)) {
      state = STATE.WIN;
      window.setTimeout(() => {
        if (state === STATE.WIN) nextLevel();
      }, 1200);
    }
  }

  function drawBackground(): void {
    ctx!.fillStyle = "#0a0a0f";
    ctx!.fillRect(0, 0, W, H);
    ctx!.strokeStyle = "rgba(139, 92, 246, 0.07)";
    ctx!.lineWidth = 1;
    for (let x = 0; x < W; x += 40) {
      ctx!.beginPath();
      ctx!.moveTo(x, 0); ctx!.lineTo(x, H);
      ctx!.stroke();
    }
    for (let y = 0; y < H; y += 40) {
      ctx!.beginPath();
      ctx!.moveTo(0, y); ctx!.lineTo(W, y);
      ctx!.stroke();
    }
  }

  function drawBricks(): void {
    ctx!.lineWidth = 2;
    for (const b of bricks) {
      if (!b.alive) continue;
      ctx!.strokeStyle = ROW_COLORS[b.row]!.stroke;
      ctx!.strokeRect(b.x + 1, b.y + 1, b.w - 2, b.h - 2);
    }
  }

  function drawPaddle(): void {
    ctx!.strokeStyle = "#22d3ee";
    ctx!.lineWidth = 2;
    roundRect(paddle.x, paddle.y, paddle.w, paddle.h, 7);
    ctx!.stroke();
  }

  function drawBall(): void {
    ctx!.fillStyle = "#ececf1";
    ctx!.beginPath();
    ctx!.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2);
    ctx!.fill();
  }

  function drawHUD(): void {
    ctx!.fillStyle = "#ececf1";
    ctx!.font = '700 16px "IBM Plex Mono", ui-monospace, monospace';
    ctx!.textAlign = "left";
    ctx!.fillText(`SCORE  ${score.toString().padStart(5, "0")}`, 16, 28);
    ctx!.textAlign = "center";
    ctx!.fillText(`LEVEL ${level}`, W / 2, 28);
    ctx!.textAlign = "right";
    ctx!.fillText(`LIVES `, W - 16 - 14 * 3, 28);
    for (let i = 0; i < lives; i++) {
      ctx!.beginPath();
      ctx!.fillStyle = "#22d3ee";
      ctx!.arc(W - 18 - i * 16, 24, 5, 0, Math.PI * 2);
      ctx!.fill();
    }

    if (state === STATE.READY) {
      ctx!.textAlign = "center";
      ctx!.fillStyle = "rgba(236, 236, 241, 0.7)";
      ctx!.font = '400 14px "IBM Plex Mono", ui-monospace, monospace';
      ctx!.fillText("Click or press SPACE to launch", W / 2, H - 80);
    }
    if (state === STATE.PAUSED) {
      ctx!.fillStyle = "rgba(10, 10, 15, 0.7)";
      ctx!.fillRect(0, 0, W, H);
      ctx!.textAlign = "center";
      ctx!.fillStyle = "#ececf1";
      ctx!.font = '700 48px "IBM Plex Mono", ui-monospace, monospace';
      ctx!.fillText("PAUSED", W / 2, H / 2);
      ctx!.font = '400 14px "IBM Plex Mono", ui-monospace, monospace';
      ctx!.fillStyle = "#a0a0b0";
      ctx!.fillText("Press P to resume", W / 2, H / 2 + 30);
    }
    if (state === STATE.WIN) {
      ctx!.textAlign = "center";
      ctx!.fillStyle = "#22d3ee";
      ctx!.font = '700 42px "IBM Plex Mono", ui-monospace, monospace';
      ctx!.fillText(`LEVEL ${level} CLEARED!`, W / 2, H / 2);
    }
  }

  function draw(): void {
    drawBackground();
    drawBricks();
    drawBall();
    drawPaddle();
    drawParticles();
    drawHUD();
  }

  function loop(): void {
    update();
    updateParticles();
    draw();
    raf = requestAnimationFrame(loop);
  }

  const onMouseMove = (e: MouseEvent): void => {
    const rect = canvas.getBoundingClientRect();
    mouseX = (e.clientX - rect.left) * (W / rect.width);
  };
  const onMouseLeave = (): void => { mouseX = null; };
  const onMouseDown = (): void => {
    if (state === STATE.READY) launchBall();
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    const k = e.key.toLowerCase();
    keys[k] = true;
    if (k === "arrowleft" || k === "arrowright" || k === "a" || k === "d") {
      // Chrome scrolls the page on arrow keys when a button has focus,
      // which "steals" some of the paddle's travel toward the walls.
      e.preventDefault();
      return;
    }
    if (e.key === " ") {
      if (state === STATE.READY) launchBall();
      e.preventDefault();
    }
    if (k === "p") {
      if (state === STATE.PLAYING) state = STATE.PAUSED;
      else if (state === STATE.PAUSED) state = STATE.PLAYING;
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keys[e.key.toLowerCase()] = false;
  };
  const onStartClick = (): void => {
    startGame();
  };

  canvas.addEventListener("mousemove", onMouseMove);
  canvas.addEventListener("mouseleave", onMouseLeave);
  canvas.addEventListener("mousedown", onMouseDown);
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  startBtn.addEventListener("click", onStartClick);

  raf = requestAnimationFrame(loop);

  cleanupFn = () => {
    if (raf !== null) cancelAnimationFrame(raf);
    raf = null;
    canvas.removeEventListener("mousemove", onMouseMove);
    canvas.removeEventListener("mouseleave", onMouseLeave);
    canvas.removeEventListener("mousedown", onMouseDown);
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
    startBtn.removeEventListener("click", onStartClick);
  };
}

function unmount(): void {
  if (cleanupFn) cleanupFn();
  cleanupFn = null;
  if (wrap) wrap.remove();
  wrap = null;
}

export const breakout: Game = {
  id: "breakout",
  title: "Breakout",
  description: "Classic brick-breaker. Mouse or arrow keys.",
  mount,
  unmount,
};
